import { useState, useEffect, useRef } from "react";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { initials, fmtDateTime } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { DuplicatePatients } from "@/components/DuplicatePatients";
import { PatientMessageDialog } from "@/components/PatientMessageDialog";
import { AutomaticTexts } from "@/components/AutomaticTexts";
import { PatientMedications } from "@/components/PatientMedications";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Mail, Send, Search, Package, Paperclip } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DELIVERY = {
  sent: { label: "✓ sent", c: "text-slate-400" },
  delivered: { label: "✓✓ delivered", c: "text-emerald-500" },
  failed: { label: "⚠ failed", c: "text-red-500" },
  simulated: { label: "demo", c: "text-slate-300" },
};

const SNIPPETS = [
  "Hi! Just checking in — are you due for your next visit?",
  "Your lab results are ready. Let's schedule a review.",
  "Reminder: your appointment is confirmed. See you soon!",
];

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function CommunicationCenter() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [letter, setLetter] = useState("");
  const [selected, setSelected] = useState(null);
  const [channel, setChannel] = useState("sms");
  const [text, setText] = useState("");
  const [fileFor, setFileFor] = useState(null);
  const scrollRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: patientPages, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["patients", debounced, letter],
    queryFn: async ({ pageParam = 0 }) => {
      const params = { offset: pageParam };
      if (debounced) params.search = debounced;
      else if (letter) params.letter = letter;
      return (await api.get("/patients", { params })).data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.length === 100 ? allPages.length * 100 : undefined),
  });
  const patients = patientPages?.pages.flat() ?? [];

  const onListScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 160 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };
  const { data: messages = [] } = useQuery({
    queryKey: ["messages", selected?.id],
    queryFn: async () => (await api.get(`/messages/${selected.id}`)).data,
    enabled: !!selected,
  });
  const { data: assignableStaff = [] } = useQuery({ queryKey: ["assignable"], queryFn: async () => (await api.get("/staff/assignable")).data });
  const { data: patientPackages = [] } = useQuery({
    queryKey: ["patient-packages", selected?.id],
    queryFn: async () => (await api.get(`/patients/${selected.id}/packages`)).data,
    enabled: !!selected,
  });
  const assign = useMutation({
    mutationFn: async (user_id) => (await api.post(`/patients/${selected.id}/assign`, { user_id })).data,
    onSuccess: (d) => { toast.success(d.assigned_to_name ? `Assigned to ${d.assigned_to_name}` : "Unassigned"); qc.invalidateQueries({ queryKey: ["patients"] }); },
  });
  const current = selected ? (patients.find((p) => p.id === selected.id) || selected) : null;

  useEffect(() => {
    if (patients.length && !selected) setSelected(patients[0]);
  }, [patients, selected]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = useMutation({
    mutationFn: async () => (await api.post("/messages", { patient_id: selected.id, channel, body: text })).data,
    onSuccess: (d) => {
      setText("");
      if (d.delivery_status === "failed") toast.error(`Text failed: ${d.fail_reason || "the texting service rejected it"}`);
      else if (d.simulated) toast.success(`${channel.toUpperCase()} saved (not sent — SMS isn't set up for this patient)`);
      else toast.success(`${channel.toUpperCase()} sent`);
      qc.invalidateQueries({ queryKey: ["messages", selected.id] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Message failed to send"),
  });

  return (
    <Layout title="Communication Center" subtitle="Unified SMS & email patient conversations" actions={<div className="flex flex-wrap gap-2"><AutomaticTexts /><DuplicatePatients /></div>}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" style={{ height: "calc(100vh - 180px)" }}>
        {/* Patient list */}
        <div className="border-r border-slate-100 flex flex-col min-h-0">
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="comm-search-input" value={search}
                onChange={(e) => { setSearch(e.target.value); if (e.target.value) setLetter(""); }}
                placeholder="Search patients…" className="pl-9 h-10" />
            </div>
          </div>
          <div className="flex min-h-0 flex-1">
            <div ref={listRef} onScroll={onListScroll} className="overflow-y-auto flex-1">
              {patients.length === 0 && (
                <div className="text-center text-xs text-slate-400 mt-8 px-4">No patients found.</div>
              )}
              {patients.map((p) => (
                <button key={p.id} data-testid={`comm-patient-${p.id}`} onClick={() => setSelected(p)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-slate-50 transition-colors ${selected?.id === p.id ? "bg-cyan-50" : "hover:bg-slate-50"}`}>
                  <div className="h-9 w-9 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">
                    {initials(`${p.first_name} ${p.last_name}`)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{p.first_name} {p.last_name}</div>
                    <div className="text-xs text-slate-400 truncate">{p.service_tags?.[0]}</div>
                  </div>
                </button>
              ))}
              {isFetchingNextPage && (
                <div className="text-center text-xs text-slate-400 py-3">Loading more…</div>
              )}
            </div>
            {/* A–Z quick jump */}
            <div data-testid="comm-az-rail" className="w-6 shrink-0 border-l border-slate-100 flex flex-col items-center justify-center py-1 select-none">
              {ALPHABET.map((ch) => (
                <button key={ch} data-testid={`comm-az-${ch}`}
                  onClick={() => { setLetter((prev) => (prev === ch ? "" : ch)); setSearch(""); if (listRef.current) listRef.current.scrollTop = 0; }}
                  className={`flex-1 w-full text-[9px] leading-none font-bold transition-colors ${letter === ch ? "text-cyan-600" : "text-slate-400 hover:text-cyan-600"}`}>
                  {ch}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Thread */}
        <div className="lg:col-span-2 flex flex-col min-h-0">
          {selected ? (
            <>
              <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">
                  {initials(`${selected.first_name} ${selected.last_name}`)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900">{selected.first_name} {selected.last_name}</div>
                  <div className="text-xs text-slate-400">{selected.phone} · {selected.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-slate-400 hidden sm:inline">Owner</span>
                  <Select value={current?.assigned_to_user || "none"}
                    onValueChange={(v) => assign.mutate(v === "none" ? null : v)}>
                    <SelectTrigger data-testid="assign-thread-select" className="h-9 w-40"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {assignableStaff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {patientPackages.length > 0 && (
                <div className="px-5 py-2.5 border-b border-slate-100 bg-cyan-50/40 flex flex-wrap gap-2" data-testid="patient-packages-strip">
                  {patientPackages.map((pk) => {
                    const low = pk.remaining > 0 && pk.remaining <= 2;
                    const lastUse = pk.uses && pk.uses.length ? new Date(pk.uses[pk.uses.length - 1]).toLocaleDateString() : "no sessions used";
                    return (
                      <div key={pk.id} data-testid={`comm-package-${pk.id}`} title={`Last used: ${lastUse}`}
                        className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${low ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-white text-slate-600 border-slate-200"}`}>
                        <Package className="h-3 w-3" /> {pk.procedure_name}: {pk.remaining}/{pk.total_sessions} left
                        <span className="text-slate-400 font-normal">· last {lastUse}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <PatientMedications patientId={current?.id} className="px-5 py-2.5 border-b border-slate-100" />

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/50">
                {messages.length === 0 && (
                  <div className="text-center text-sm text-slate-400 mt-10">No messages yet. Start the conversation below.</div>
                )}
                <AnimatePresence initial={false}>
                  {messages.map((m) => {
                    const inbound = m.direction === "inbound";
                    return (
                      <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        className={`flex ${inbound ? "justify-start" : "justify-end"}`}>
                        <div className="max-w-[75%]">
                          <div className={inbound
                            ? "bg-white border border-slate-200 text-slate-800 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm"
                            : "bg-slate-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm"}>{m.body}</div>
                          <div className={`text-[10px] text-slate-400 mt-1 flex items-center gap-1 ${inbound ? "" : "justify-end text-right"}`}>
                            {m.channel === "sms" ? <MessageSquare className="h-2.5 w-2.5" /> : <Mail className="h-2.5 w-2.5" />}
                            {inbound ? "Received" : m.channel.toUpperCase()} · {fmtDateTime(m.created_at)}
                            {!inbound && m.delivery_status && (
                              <span title={m.fail_reason || undefined} className={`ml-1 ${(DELIVERY[m.delivery_status] || DELIVERY.sent).c}`}>· {(DELIVERY[m.delivery_status] || DELIVERY.sent).label}{m.fail_reason ? ` — ${m.fail_reason}` : ""}</span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>

              <div className="border-t border-slate-100 p-4">
                <div className="flex gap-2 mb-2 flex-wrap">
                  {SNIPPETS.map((s, i) => (
                    <button key={i} data-testid={`comm-snippet-${i}`} onClick={() => setText(s)}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors">
                      {s.slice(0, 32)}…
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                    <button data-testid="comm-channel-sms" onClick={() => setChannel("sms")}
                      className={`px-3 text-xs font-semibold transition-colors ${channel === "sms" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
                      <MessageSquare className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid="comm-channel-email" onClick={() => setChannel("email")}
                      className={`px-3 text-xs font-semibold transition-colors ${channel === "email" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
                      <Mail className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <Button data-testid="comm-attach-button" variant="outline" title="Send a video or file" className="h-11 px-3"
                    onClick={() => setFileFor({ id: current.id, patient_name: `${current.first_name || ""} ${current.last_name || ""}`.trim(), patient_phone: current.phone })}>
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Input data-testid="comm-message-input" value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && text.trim()) send.mutate(); }}
                    placeholder={`Type a ${channel.toUpperCase()} message…`} className="flex-1 h-11" />
                  <Button data-testid="comm-send-button" disabled={!text.trim() || send.isPending} onClick={() => send.mutate()}
                    className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-4">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">Select a patient</div>
          )}
        </div>
      </div>
      <PatientMessageDialog appt={fileFor} onClose={() => setFileFor(null)} endpoint={fileFor ? `/patients/${fileFor.id}/send-message` : undefined}
        onSent={() => qc.invalidateQueries({ queryKey: ["messages", fileFor?.id] })} />
    </Layout>
  );
}
